import theme from 'src/theme';

export const inputSx = {
    root: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1.5,
        px: 3,
        py: 2,
        bgcolor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(0, 0, 0, 0.06)',
    },
    textField: {
        flex: 1,
        '& .MuiOutlinedInput-root': {
            borderRadius: '14px',
            bgcolor: '#F2F4F7',
            transition: 'all 0.2s ease',
            '& fieldset': {
                borderColor: 'transparent',
                transition: 'all 0.2s ease',
            },
            '&:hover': {
                bgcolor: '#ECEEF2',
            },
            '&:hover fieldset': {
                borderColor: 'transparent',
            },
            '&.Mui-focused': {
                bgcolor: theme.palette.common.white,
                boxShadow: `0 0 0 3px rgba(0, 127, 220, 0.1)`,
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: '1.5px',
            },
        },
    },
    rootElevated: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1.5,
        px: 2,
        py: 1.5,
        bgcolor: theme.palette.common.white,
        border: '1.5px solid rgba(0, 127, 220, 0.15)',
        borderRadius: '18px',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:focus-within': {
            boxShadow: '0 6px 32px rgba(0, 127, 220, 0.15), 0 0 0 3px rgba(0, 127, 220, 0.08)',
            borderColor: 'rgba(0, 127, 220, 0.4)',
        },
    },
    textFieldElevated: {
        flex: 1,
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            bgcolor: 'transparent',
            fontSize: '1rem',
            '& fieldset': {borderColor: 'transparent'},
            '&:hover': {bgcolor: 'transparent'},
            '&:hover fieldset': {borderColor: 'transparent'},
            '&.Mui-focused': {bgcolor: 'transparent', boxShadow: 'none'},
            '&.Mui-focused fieldset': {borderColor: 'transparent'},
        },
    },
    // Pulses like the stop button once there's a message ready to send. Softer ring
    // than stop's (0.35 vs 0.45) — an invitation, not an interruption.
    sendButton: {
        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        color: theme.palette.common.white,
        width: 44,
        height: 44,
        borderRadius: '14px',
        boxShadow: '0 2px 6px rgba(0, 127, 220, 0.25)',
        transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
        animation: 'sendPulse 2s ease-out infinite',
        '@keyframes sendPulse': {
            '0%': {boxShadow: '0 2px 6px rgba(0, 127, 220, 0.25), 0 0 0 0 rgba(0, 127, 220, 0.35)'},
            '70%': {boxShadow: '0 2px 6px rgba(0, 127, 220, 0.25), 0 0 0 9px rgba(0, 127, 220, 0)'},
            '100%': {boxShadow: '0 2px 6px rgba(0, 127, 220, 0.25), 0 0 0 0 rgba(0, 127, 220, 0)'},
        },
        '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.main})`,
            boxShadow: '0 4px 12px rgba(0, 127, 220, 0.35)',
            transform: 'translateY(-1px)',
            animation: 'none', // pointer is already on it — the pulse has done its job
        },
        '&:active': {
            transform: 'translateY(0)',
        },
        // Empty field / initializing — nothing to send, so nothing to draw the eye to.
        '&.Mui-disabled': {
            background: '#E0E2E7',
            color: '#A0A3AD',
            boxShadow: 'none',
            animation: 'none',
        },
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
        },
    },
    // Replaces the send button while the agent is answering. Same geometry as send so
    // the composer doesn't shift, but red rather than blue: it has to read instantly as
    // "interrupt", and it is the only live control on screen at that moment. The halo
    // pulses while the agent works — the button appears mid-answer, when the user's
    // eyes are on the streaming text, so it needs to announce itself.
    stopButton: {
        background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
        color: theme.palette.common.white,
        width: 44,
        height: 44,
        borderRadius: '14px',
        boxShadow: '0 2px 8px rgba(230, 41, 48, 0.35)',
        transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
        animation: 'stopPulse 2s ease-out infinite',
        '@keyframes stopPulse': {
            '0%': {boxShadow: '0 2px 8px rgba(230, 41, 48, 0.35), 0 0 0 0 rgba(230, 41, 48, 0.45)'},
            '70%': {boxShadow: '0 2px 8px rgba(230, 41, 48, 0.35), 0 0 0 9px rgba(230, 41, 48, 0)'},
            '100%': {boxShadow: '0 2px 8px rgba(230, 41, 48, 0.35), 0 0 0 0 rgba(230, 41, 48, 0)'},
        },
        '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.error.dark}, ${theme.palette.brand.main})`,
            boxShadow: '0 4px 14px rgba(230, 41, 48, 0.45)',
            transform: 'translateY(-1px)',
            animation: 'none', // pointer is already on it — the pulse has done its job
        },
        '&:active': {
            transform: 'translateY(0)',
        },
        // Stop already requested: the run is winding down and the button does nothing
        // more, so drop the pulse and the urgency with it. Stays red-tinted rather than
        // going grey — it's still the stop control, just spent, and white-on-grey would
        // leave the spinner barely readable.
        '&.Mui-disabled': {
            background: theme.palette.error.light,
            color: theme.palette.common.white,
            opacity: 0.7,
            boxShadow: 'none',
            animation: 'none',
        },
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
        },
    },
};